/**
 * <DisciplineCoachingBlock />
 *
 * Mounted on every refusal surface. One component, one source of truth.
 * Pulls the coaching prompt for the (rule, scenario) pair, surfaces the
 * Defend dialog, the Request override action, and any active override or
 * pending follow-up state for the entity.
 *
 * No exclamation points. No emojis. "Maybe" is banned — Yes / No / Unknown.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/session";

type RuleCode =
  | "RULE-FORECAST-1"
  | "RULE-ACTIVITY-1"
  | "RULE-GATE0"
  | "RULE-EVIDENCE"
  | "RULE-CONV-BAND"
  | "RULE-MKT-APPROVAL"
  | "RULE-DERAIL-1";

type EntityKind = "account" | "opportunity" | "activity" | "forecast_period";

type CoachingPrompt = {
  id: string;
  rule_code: string;
  scenario_key: string;
  coaching_title: string;
  coaching_body: string;
  suggested_action: string;
};

type ActiveOverride = {
  id: string;
  rule_code: string;
  reason: string;
  expires_at: string;
  status: "active";
};

type PendingFollowup = {
  id: string;
  due_at: string;
  escalate_to_manager_at: string;
  status: "open";
};

interface Props {
  ruleCode: RuleCode;
  entityKind: EntityKind;
  entityId: string;
  scenarioKey: string;
  refusalMessage: string;
  attemptedAction?: unknown;
  refusalPayload?: unknown;
  onDefenseSubmitted?: () => void;
  onOverrideRequested?: () => void;
  className?: string;
}

const REP_REQUESTABLE = new Set(["RULE-GATE0", "RULE-EVIDENCE"]);
const NEVER_OVERRIDABLE = new Set(["RULE-MKT-APPROVAL"]);

export function DisciplineCoachingBlock(props: Props) {
  const {
    ruleCode,
    entityKind,
    entityId,
    scenarioKey,
    refusalMessage,
    attemptedAction,
    refusalPayload,
    onDefenseSubmitted,
    onOverrideRequested,
    className,
  } = props;

  const [coaching, setCoaching] = useState<CoachingPrompt | null>(null);
  const [activeOverride, setActiveOverride] = useState<ActiveOverride | null>(null);
  const [pendingFollowup, setPendingFollowup] = useState<PendingFollowup | null>(null);
  const [defending, setDefending] = useState(false);
  const [defenseText, setDefenseText] = useState("");
  const [requestingOverride, setRequestingOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canRequestOverride = useMemo(
    () => REP_REQUESTABLE.has(ruleCode) && !NEVER_OVERRIDABLE.has(ruleCode),
    [ruleCode],
  );

  const loadContext = useCallback(async () => {
    try {
      const [cRes, oRes, fRes] = await Promise.all([
        api(
          `/api/discipline/coaching-prompt?rule_code=${encodeURIComponent(ruleCode)}&scenario_key=${encodeURIComponent(scenarioKey)}`,
        ),
        api(
          `/api/discipline/overrides/active?rule_code=${encodeURIComponent(ruleCode)}&entity_kind=${encodeURIComponent(entityKind)}&entity_id=${encodeURIComponent(entityId)}`,
        ),
        api(
          `/api/discipline/followups?scope=self&status=open`,
        ),
      ]);
      const c = await cRes.json();
      const o = await oRes.json();
      const f = await fRes.json();
      setCoaching(c?.prompt ?? null);
      setActiveOverride(o?.override ?? null);
      const mine = (f?.followups ?? []).find(
        (row: any) =>
          row.rule_code === ruleCode &&
          row.entity_kind === entityKind &&
          row.entity_id === entityId,
      );
      setPendingFollowup(mine ?? null);
    } catch (e: any) {
      console.error("[DisciplineCoachingBlock] load context", e);
    }
  }, [ruleCode, entityKind, entityId, scenarioKey]);

  useEffect(() => {
    void loadContext();
  }, [loadContext]);

  async function submitDefense() {
    if (!defenseText.trim()) {
      setError("Enter a defense.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await api(`/api/discipline/defenses`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          rule_code: ruleCode,
          entity_kind: entityKind,
          entity_id: entityId,
          attempted_action: attemptedAction ?? {},
          refusal_payload: refusalPayload ?? { message: refusalMessage },
          defense_text: defenseText.trim(),
        }),
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body?.error ?? "Failed to submit defense.");
      setDefending(false);
      setDefenseText("");
      onDefenseSubmitted?.();
      await loadContext();
    } catch (e: any) {
      console.error("[DisciplineCoachingBlock] submit defense", e);
      setError(e?.message ?? "Failed to submit defense.");
    } finally {
      setBusy(false);
    }
  }

  async function requestOverride() {
    if (!overrideReason.trim()) {
      setError("Enter a reason.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await api(`/api/discipline/overrides`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          rule_code: ruleCode,
          entity_kind: entityKind,
          entity_id: entityId,
          reason: overrideReason.trim(),
        }),
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body?.error ?? "Failed to request override.");
      setRequestingOverride(false);
      setOverrideReason("");
      onOverrideRequested?.();
      await loadContext();
    } catch (e: any) {
      console.error("[DisciplineCoachingBlock] request override", e);
      setError(e?.message ?? "Failed to request override.");
    } finally {
      setBusy(false);
    }
  }

  async function completeFollowup() {
    if (!pendingFollowup) return;
    setBusy(true);
    try {
      await api(`/api/discipline/followups/${pendingFollowup.id}/complete`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ completed_action_summary: "Marked complete from refusal surface." }),
      });
      setPendingFollowup(null);
    } catch (e: any) {
      console.error("[DisciplineCoachingBlock] complete followup", e);
    } finally {
      setBusy(false);
    }
  }

  const overrideUntil = activeOverride
    ? new Date(activeOverride.expires_at).toLocaleString()
    : null;

  return (
    <div
      data-testid={`discipline-coaching-block-${ruleCode}`}
      className={className}
      style={{
        border: "1px solid #DC2626",
        background: "#FFF7F7",
        borderRadius: 8,
        padding: 12,
        marginTop: 12,
        marginBottom: 12,
        color: "#0B0B0F",
        fontSize: 13,
      }}
    >
      <div style={{ fontWeight: 700, color: "#DC2626", marginBottom: 6 }}>
        Refused by {ruleCode}
      </div>
      <div style={{ marginBottom: 8 }}>{refusalMessage}</div>

      {coaching && (
        <div
          style={{
            background: "#FAFAF7",
            borderLeft: "3px solid #0B0B0F",
            padding: 10,
            marginBottom: 10,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 4 }}>{coaching.coaching_title}</div>
          <div style={{ marginBottom: 6 }}>{coaching.coaching_body}</div>
          <div style={{ fontStyle: "italic", color: "#444" }}>
            Suggested: {coaching.suggested_action}
          </div>
        </div>
      )}

      {activeOverride && (
        <div
          data-testid="override-active-badge"
          style={{
            display: "inline-block",
            background: "#F0EBFF",
            color: "#3A1F8F",
            border: "1px solid #B8A6F0",
            borderRadius: 6,
            padding: "4px 8px",
            marginBottom: 8,
          }}
        >
          Override active until {overrideUntil}.{" "}
          <a href="#/admin/discipline" style={{ textDecoration: "underline" }}>
            View
          </a>
        </div>
      )}

      {pendingFollowup && (
        <div
          data-testid="pending-followup"
          style={{
            display: "block",
            background: "#FFF8E1",
            border: "1px solid #E6C300",
            borderRadius: 6,
            padding: 8,
            marginBottom: 8,
          }}
        >
          Follow-up due {new Date(pendingFollowup.due_at).toLocaleString()}.{" "}
          <button
            type="button"
            onClick={completeFollowup}
            disabled={busy}
            style={{
              background: "transparent",
              border: 0,
              color: "#0B0B0F",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            Mark complete
          </button>
        </div>
      )}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {!defending && (
          <button
            type="button"
            data-testid="open-defend-btn"
            onClick={() => setDefending(true)}
            disabled={busy}
            style={{
              background: "transparent",
              border: 0,
              color: "#0B0B0F",
              textDecoration: "underline",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Defend this
          </button>
        )}
        {!NEVER_OVERRIDABLE.has(ruleCode) && canRequestOverride && !requestingOverride && !activeOverride && (
          <button
            type="button"
            data-testid="request-override-btn"
            onClick={() => setRequestingOverride(true)}
            disabled={busy}
            style={{
              background: "#0B0B0F",
              color: "#FAFAF7",
              border: 0,
              padding: "6px 10px",
              borderRadius: 6,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Request override
          </button>
        )}
      </div>

      {defending && (
        <div style={{ marginTop: 10 }}>
          <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>
            Why should this be allowed
          </label>
          <textarea
            value={defenseText}
            onChange={(e) => setDefenseText(e.target.value)}
            rows={3}
            data-testid="defense-text-input"
            style={{ width: "100%", padding: 8, border: "1px solid #ccc", borderRadius: 6 }}
            placeholder="State the facts. Numbers and names, not opinions."
          />
          <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
            <button
              type="button"
              data-testid="submit-defense-btn"
              onClick={submitDefense}
              disabled={busy || !defenseText.trim()}
              style={{
                background: "#0B0B0F",
                color: "#FAFAF7",
                border: 0,
                padding: "6px 10px",
                borderRadius: 6,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Submit defense
            </button>
            <button
              type="button"
              onClick={() => {
                setDefending(false);
                setDefenseText("");
              }}
              disabled={busy}
              style={{
                background: "transparent",
                border: "1px solid #ccc",
                padding: "6px 10px",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {requestingOverride && (
        <div style={{ marginTop: 10 }}>
          <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>
            Reason for override
          </label>
          <textarea
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
            rows={3}
            data-testid="override-reason-input"
            style={{ width: "100%", padding: 8, border: "1px solid #ccc", borderRadius: 6 }}
            placeholder="State the exception. Manager will approve or deny."
          />
          <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
            <button
              type="button"
              data-testid="submit-override-btn"
              onClick={requestOverride}
              disabled={busy || !overrideReason.trim()}
              style={{
                background: "#DC2626",
                color: "#FAFAF7",
                border: 0,
                padding: "6px 10px",
                borderRadius: 6,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Submit request
            </button>
            <button
              type="button"
              onClick={() => {
                setRequestingOverride(false);
                setOverrideReason("");
              }}
              disabled={busy}
              style={{
                background: "transparent",
                border: "1px solid #ccc",
                padding: "6px 10px",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && (
        <div role="alert" style={{ color: "#DC2626", marginTop: 6 }}>
          {error}
        </div>
      )}
    </div>
  );
}

export default DisciplineCoachingBlock;
